import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const LiveAuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleCallback = async () => {
      // Check for old-style callback (has token directly)
      const directToken = searchParams.get('token');
      const directCustomerId = searchParams.get('customer_id');
      
      // New-style callback (has google_access_token, needs /complete call)
      const googleAccessToken = searchParams.get('google_access_token');
      const email = searchParams.get('email');
      const name = searchParams.get('name');
      const picture = searchParams.get('picture');
      const googleId = searchParams.get('google_id');
      const redirectTo = searchParams.get('redirect_to');
      const fromDomain = searchParams.get('from');

      let token, customerId, isAdmin;

      if (directToken && directCustomerId) {
        // Old-style: token already provided
        token = directToken;
        customerId = directCustomerId;
        isAdmin = searchParams.get('is_admin') === 'true';
      } else if (googleAccessToken && email) {
        // New-style: call /complete to get JWT
        try {
          const res = await axios.post(`${API}/api/auth/google/complete`, {
            email: decodeURIComponent(email),
            name: decodeURIComponent(name || ''),
            picture: decodeURIComponent(picture || ''),
            google_id: decodeURIComponent(googleId || ''),
            google_access_token: googleAccessToken
          });
          token = res.data.token;
          customerId = res.data.customer_id;
          isAdmin = res.data.is_admin;
        } catch (err) {
          console.error('Complete login error:', err);
          setError('Login failed. Please try again.');
          setTimeout(() => navigate('/live/login?error=complete_failed'), 2000);
          return;
        }
      } else {
        navigate('/live/login?error=auth_failed');
        return;
      }

      // Store auth data
      localStorage.setItem('livestreamToken', token);
      localStorage.setItem('livestreamCustomer', JSON.stringify({
        id: customerId,
        email: decodeURIComponent(email || searchParams.get('email') || ''),
        full_name: decodeURIComponent(name || searchParams.get('name') || ''),
        profile_picture: decodeURIComponent(picture || searchParams.get('picture') || ''),
        is_admin: isAdmin
      }));

      // Handle redirects
      const creatorPageReturn = sessionStorage.getItem('creatorPageReturn');

      if (fromDomain === 'ltvvod') {
        let redirectPath = redirectTo ? decodeURIComponent(redirectTo) : (creatorPageReturn || '/vod');
        sessionStorage.removeItem('creatorPageReturn');
        // On ltvvod.com, the VOD homepage is at root '/' not '/vod'
        if (window.location.hostname.includes('ltvvod') && (redirectPath === '/vod' || redirectPath === '/vod/')) {
          redirectPath = '/';
        }
        window.location.href = `${window.location.origin}${redirectPath}`;
        return;
      }
      
      if (fromDomain === 'vod') {
        let redirectPath = redirectTo ? decodeURIComponent(redirectTo) : (creatorPageReturn || '/vod');
        sessionStorage.removeItem('creatorPageReturn');
        navigate(redirectPath);
        return;
      }

      if (redirectTo) {
        sessionStorage.removeItem('creatorPageReturn');
        navigate(decodeURIComponent(redirectTo));
        return;
      }
      
      if (creatorPageReturn) {
        sessionStorage.removeItem('creatorPageReturn');
        navigate(creatorPageReturn);
        return;
      }

      const homepageRedirect = sessionStorage.getItem('homepageLoginRedirect');
      if (homepageRedirect) {
        sessionStorage.removeItem('homepageLoginRedirect');
        navigate(homepageRedirect);
        return;
      }

      const intendedEvent = localStorage.getItem('intendedEvent');
      const intendedAction = localStorage.getItem('intendedEventAction');
      const intendedPromo = localStorage.getItem('intendedPromo');
      if (intendedEvent) {
        localStorage.removeItem('intendedEvent');
        localStorage.removeItem('intendedEventAction');
        localStorage.removeItem('intendedPromo');
        let redirectUrl = `/live/event/${intendedEvent}`;
        if (intendedAction === 'checkout') {
          redirectUrl += '?auto_checkout=true';
          if (intendedPromo) redirectUrl += `&promo=${intendedPromo}`;
        }
        navigate(redirectUrl);
        return;
      }
      
      if (isAdmin) {
        navigate('/admin/livestream');
      } else {
        navigate('/live/my-tickets');
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div data-testid="auth-callback-page" className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-white mx-auto mb-4" />
        <p className="text-white text-lg">{error || 'Signing you in...'}</p>
      </div>
    </div>
  );
};

export default LiveAuthCallback;
